import { inject } from "@angular/core";
import {
    HttpInterceptorFn,
    HttpRequest,
    HttpHandlerFn,
    HttpEvent,
    HttpErrorResponse,
} from "@angular/common/http";
import { Observable, throwError, BehaviorSubject } from "rxjs";
import { catchError, switchMap, filter, take } from "rxjs/operators";
import { AuthService } from "../services/auth.service";

let isRefreshing = false;
let refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

export const authInterceptor: HttpInterceptorFn = (
    request: HttpRequest<any>,
    next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
    const authService = inject(AuthService);

    // Don't add token to auth endpoints
    if (isAuthEndpoint(request.url)) {
        return next(request);
    }

    // Add authorization header if token exists
    const authRequest = addTokenToRequest(request, authService);

    return next(authRequest).pipe(
        catchError((error: HttpErrorResponse) => {
            // Handle 401 Unauthorized errors
            if (error.status === 401 && !isAuthEndpoint(request.url)) {
                return handle401Error(authRequest, next, authService);
            }

            return throwError(() => error);
        })
    );
};

function addTokenToRequest(
    request: HttpRequest<any>,
    authService: AuthService
): HttpRequest<any> {
    const authHeader = authService.getAuthorizationHeader();

    if (authHeader) {
        return request.clone({
            setHeaders: {
                Authorization: authHeader,
            },
        });
    }

    return request;
}

function handle401Error(
    request: HttpRequest<any>,
    next: HttpHandlerFn,
    authService: AuthService
): Observable<HttpEvent<any>> {
    if (!isRefreshing) {
        isRefreshing = true;
        refreshTokenSubject.next(null);

        return authService.refreshToken().pipe(
            switchMap(() => {
                isRefreshing = false;
                refreshTokenSubject.next(true);

                // Retry the original request with new token
                const newAuthRequest = addTokenToRequest(request, authService);
                return next(newAuthRequest);
            }),
            catchError((error) => {
                isRefreshing = false;
                authService.logout();
                return throwError(() => error);
            })
        );
    } else {
        // Wait for token refresh to complete
        return refreshTokenSubject.pipe(
            filter((result) => result !== null),
            take(1),
            switchMap(() => {
                const newAuthRequest = addTokenToRequest(request, authService);
                return next(newAuthRequest);
            })
        );
    }
}

function isAuthEndpoint(url: string): boolean {
    return (
        url.includes("/auth/login") ||
        url.includes("/auth/register") ||
        url.includes("/auth/refresh") ||
        url.includes("/auth/forgot-password") ||
        url.includes("/auth/reset-password") ||
        url.includes("/auth/verify-email") ||
        url.includes("/auth/resend-verification")
    );
}
